import React, { createContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogin = (newToken, userDataFromApi = null) => {
    if (!newToken) return null;

    setToken(newToken);
    localStorage.setItem("user_token", newToken);

    try {
      const decoded = jwtDecode(newToken);

      const mergedUser = {
        ...decoded,
        ...userDataFromApi,
        userId: userDataFromApi?.id || decoded?.id || decoded?.sub,
        fullName: userDataFromApi?.fullName,
        phone: userDataFromApi?.phone,
        hasPin: userDataFromApi?.hasPin,
        email: userDataFromApi?.email || decoded?.email,
      };

      localStorage.setItem("user", JSON.stringify(mergedUser));
      setUser(mergedUser);
      toast.success("Login successful");
      return mergedUser;
    } catch (err) {
      console.error("Token decode failed:", err);
      toast.error("Login failed – invalid token format");
      return null;
    }
  };

  const handleLogout = (silent = false) => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("user_token");

    if (!silent) toast.info("You have been logged out");
  };

  /**
   * Patches the in-memory + persisted user object without a full
   * re-login. This is the piece that was missing: editing a profile on
   * the Profile screen updated the backend (and that screen's own local
   * state) but never touched this context. Every OTHER screen reading
   * `user` via useAuth() — like AddFundPageOne, through
   * useProfileCompletion — kept seeing the stale, incomplete user
   * object from whenever the person last logged in, even after the
   * profile was genuinely completed. Any screen that edits or refreshes
   * profile data should call this so the rest of the app sees it too.
   */
  const updateUser = (updates) => {
    setUser((prev) => {
      const merged = { ...(prev || {}), ...updates };
      localStorage.setItem("user", JSON.stringify(merged));
      return merged;
    });
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("user_token");
    const storedUserJson = localStorage.getItem("user");

    if (storedToken && storedUserJson) {
      try {
        const decoded = jwtDecode(storedToken);

        if (decoded.exp * 1000 < Date.now()) {
          // Token expired — clear everything
          handleLogout(true);
          toast.info("Session expired. Please sign in again.");
        } else {
          setToken(storedToken);
          setUser(JSON.parse(storedUserJson));
        }
      } catch (err) {
        console.error("Session restore failed:", err);
        handleLogout(true);
      }
    }

    setLoading(false);
  }, []);

  const value = {
    token,
    user,
    loading,
    login: handleLogin,
    logout: handleLogout,
    // Use this instead of reaching into setUser directly — it keeps
    // localStorage in sync, so a page refresh doesn't undo the update.
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
